/**
 * Shared matchers for errors that should not be reported to Sentry.
 * Used by handleError, captureError, captureMessage, and Sentry beforeSend
 * so expected user/business outcomes are filtered in one place.
 */

export function getErrorMessage(error: unknown): string {
  if (error instanceof Error) return error.message;
  if (typeof error === 'string') return error;
  if (error && typeof error === 'object' && 'message' in error) {
    return String((error as { message: unknown }).message);
  }
  return String(error);
}

function normalizeForMatch(error: unknown): string {
  return getErrorMessage(error).toLowerCase();
}

function getErrorName(error: unknown): string {
  if (error && typeof error === 'object' && 'name' in error) {
    return String((error as { name?: unknown }).name || '').toLowerCase();
  }
  return '';
}

/** Substrings of expected user/domain outcomes already shown in the UI. */
export const EXPECTED_USER_ERROR_PATTERNS = [
  'entry deadline has passed',
  'the competition has kicked off',
  'the tournament has kicked off',
  'payment was cancelled',
  'payment was canceled',
  'payment required to save your pick',
  'you already have an entry with this nickname',
  'competition id is required',
] as const;

/**
 * Expected user/domain outcomes that the UI already surfaces.
 * These are not app bugs and should not create Sentry issues.
 */
export function isExpectedUserError(error: unknown): boolean {
  const message = normalizeForMatch(error);
  const combined = `${getErrorName(error)}: ${message}`;

  if (EXPECTED_USER_ERROR_PATTERNS.some((pattern) => message.includes(pattern) || combined.includes(pattern))) {
    return true;
  }

  // Stale JWT / auth.uid() mismatch on SECURITY DEFINER RPCs (FOOTYPICKS-E).
  // Match the exact RPC message, not every string containing "unauthorized".
  if (message === 'unauthorized' || combined === 'supabaseerror: unauthorized') {
    return true;
  }

  return false;
}

/**
 * Network connectivity errors caused by the user's connection, not app bugs.
 */
export function isNetworkError(error: unknown): boolean {
  const message = normalizeForMatch(error);

  const networkErrorPatterns = [
    'failed to fetch',
    'networkerror',
    'network error',
    'network request failed',
    'load failed',
    'net::err_',
    'the internet connection appears to be offline',
    'the network connection was lost',
    'a server with the specified hostname could not be found',
    'the request timed out',
    'could not connect to the server',
    'econnrefused',
    'enotfound',
    'etimedout',
    'econnreset',
    'signal is aborted',
    'aborterror',
    'the operation was aborted',
    'aborted without reason',
    'the user aborted a request',
    'failed to send a request to the edge function',
  ];

  return networkErrorPatterns.some((pattern) => message.includes(pattern));
}

/**
 * Transient Supabase/PostgREST backend blips (schema cache reload, gateway hiccup).
 */
export function isTransientSupabaseError(error: unknown): boolean {
  const message = normalizeForMatch(error);
  const code =
    error && typeof error === 'object' && 'code' in error
      ? String((error as { code?: unknown }).code ?? '').toLowerCase()
      : '';

  const transientPatterns = [
    'could not query the database for the schema cache',
    'schema cache',
    '502 bad gateway',
    '503 service unavailable',
    '504 gateway timeout',
  ];

  if (transientPatterns.some((p) => message.includes(p))) return true;
  if (code === 'pgrst002' || code === 'pgrst001') return true;
  return false;
}

/**
 * Intentional empty-state diagnostics that were captured as Sentry issues.
 * These belong as breadcrumbs, not errors.
 */
export function isDiagnosticSentryMessage(message: string): boolean {
  const lower = message.toLowerCase();
  return (
    lower.includes('no active competitions visible') ||
    lower.includes('nocompetitionstate shown for authenticated user')
  );
}

export function shouldIgnoreSentryException(error: unknown): boolean {
  return isNetworkError(error) || isTransientSupabaseError(error) || isExpectedUserError(error);
}

/** Regexes / strings for Sentry.init `ignoreErrors`. */
export const SENTRY_IGNORE_ERRORS: Array<string | RegExp> = [
  'ResizeObserver',
  'Non-Error promise rejection',
  'Loading chunk',
  'Failed to fetch dynamically imported module',
  /Extension context invalidated/,
  /chrome-extension/,
  /moz-extension/,
  /Importing a module script failed/,
  /not a valid JavaScript MIME type/,
  /Failed to fetch/,
  /NetworkError/,
  /Network request failed/,
  /Load failed/,
  /net::ERR_/,
  /The Internet connection appears to be offline/,
  /The network connection was lost/,
  /request timed out/i,
  /ECONNREFUSED/,
  /ETIMEDOUT/,
  /ECONNRESET/,
  /signal is aborted without reason/i,
  /navigatorLock.*acquire timed out/i,
  /^AbortError$/,
  /^AbortError:\s*The operation was aborted\.$/i,
  /the operation was aborted/i,
  /Error invoking log: Java bridge/i,
  /runtime\.sendMessage\(\)\. Tab not found/i,
  /Entry deadline has passed/i,
  /Payment was cancelled/i,
  /Payment was canceled/i,
  /Payment required to save your pick/i,
  /You already have an entry with this nickname/i,
  /Competition ID is required/i,
  /^Unauthorized$/i,
  /Failed to send a request to the Edge Function/i,
  /OrganizationLandingPage: no active competitions visible/i,
  /NoCompetitionState shown for authenticated user/i,
];
